/**
 * VoiceLoginPanel — drop-in replacement for the disabled "Voice" tab in LoginPage.
 * Lets a user speak their enrolled phrase and authenticates via /api/voice/login (Sarvam STT).
 *
 * Props:
 *   onSuccess({ access_token, user_summary, globalContext, transcript })  — called on successful auth
 *   onSwitchToPassword(usernameToPrefill)                                 — fallback to password tab
 *   t (translator), API_BASE                                              — i18n + base URL
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Mic, Square, RefreshCw, CheckCircle2, AlertCircle, ChevronRight, KeyRound } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { useAudioRecorder, blobToBase64 } from '../hooks/useAudioRecorder';
import { API_BASE as _API_BASE } from '../../lib/api';

export default function VoiceLoginPanel({ onSuccess, onSwitchToPassword, API_BASE }) {
  const [username, setUsername] = useState('');
  const [profile, setProfile] = useState(null); // { voice_enabled, voice_phrase, ... }
  const [statusLoading, setStatusLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState('username'); // 'username' | 'record' | 'verifying'
  const [transcript, setTranscript] = useState('');
  const { isRecording, recordingTime, startRecording, stopRecording, cancelRecording, error: recError } = useAudioRecorder();

  const apiUrl = API_BASE || _API_BASE;

  const checkStatus = useCallback(async (uname) => {
    if (!uname) return;
    setStatusLoading(true);
    try {
      const r = await fetch(`${apiUrl}/api/voice/status?username=${encodeURIComponent(uname)}`);
      const d = await r.json();
      if (d?.voice_enabled) {
        setProfile(d);
        setStep('record');
      } else {
        setProfile(null);
        toast.error('No voice profile found for this user. Use Password tab instead.');
      }
    } catch (e) {
      toast.error('Could not reach voice service.');
    } finally {
      setStatusLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => () => cancelRecording(), [cancelRecording]);

  const handleStartRecord = async () => {
    setTranscript('');
    try { await startRecording(); }
    catch (e) { toast.error(e?.message || 'Microphone permission denied.'); }
  };

  const handleStopAndSubmit = async () => {
    try {
      const blob = await stopRecording();
      if (!blob || blob.size < 1000) { toast.error('Recording too short. Please try again.'); return; }
      setSubmitting(true);
      setStep('verifying');
      const audio_base64 = await blobToBase64(blob);
      const r = await fetch(`${apiUrl}/api/voice/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, audio_base64, language_code: 'en-IN' }),
      });
      const d = await r.json();
      if (!r.ok || !d?.success) {
        setTranscript(d?.transcript || '');
        toast.error(d?.detail || d?.error || 'Voice authentication failed.');
        setStep('record');
        return;
      }
      setTranscript(d.transcript || '');
      toast.success(`Voice authenticated · ${(d.similarity ? (d.similarity * 100).toFixed(0) + '% match' : 'OK')}`);
      onSuccess && onSuccess(d);
    } catch (e) {
      toast.error(e?.message || 'Voice login failed.');
      setStep('record');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-2.5" data-testid="voice-login-panel">
      {step === 'username' && (
        <>
          <div className="flex flex-col items-center text-center pt-1 pb-2">
            <div className="w-12 h-12 rounded-2xl bg-[#3DD56D]/15 dark:bg-[#3DD56D]/20 border border-[#3DD56D]/40 flex items-center justify-center mb-2">
              <Mic className="text-[#3DD56D]" size={20} />
            </div>
            <p className="text-sm dark:text-neutral-200 text-gray-800 font-semibold">Voice Login</p>
            <p className="text-[11px] dark:text-neutral-400 text-gray-500 mt-0.5">Powered by Sarvam AI · Speak your enrolled phrase to authenticate.</p>
          </div>
          <div>
            <Input
              data-testid="voice-username-input"
              type="text"
              placeholder="Enter your username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && username) checkStatus(username); }}
              autoFocus
              className="h-9 rounded-xl text-sm dark:bg-white/[0.05] bg-gray-50 dark:border-white/[0.09] border-gray-200 dark:text-white text-gray-900 dark:placeholder:text-neutral-500 placeholder:text-gray-400 focus-visible:ring-[#3DD56D] focus-visible:ring-1"
            />
          </div>
          <Button
            type="button"
            data-testid="voice-continue-button"
            disabled={!username || statusLoading}
            onClick={() => checkStatus(username)}
            className="w-full h-9 rounded-xl bg-[#3DD56D] hover:bg-[#4ce07c] text-black font-semibold shadow-[0_4px_18px_rgba(61,213,109,0.26)] disabled:opacity-50 transition-colors text-sm"
          >
            {statusLoading
              ? <span className="flex items-center gap-1.5"><RefreshCw className="rcq-spin" size={14} /> Checking…</span>
              : <span className="flex items-center gap-1.5">Continue <ChevronRight size={14} /></span>}
          </Button>
          <button
            type="button"
            data-testid="voice-switch-to-password"
            onClick={() => onSwitchToPassword && onSwitchToPassword(username)}
            className="w-full text-center text-[11px] dark:text-neutral-500 text-gray-500 hover:text-[#3DD56D] transition-colors mt-1 flex items-center justify-center gap-1"
          >
            <KeyRound size={11} /> Use password instead
          </button>
        </>
      )}

      {step === 'record' && profile && (
        <>
          <div className="flex flex-col items-center text-center pt-1">
            <Badge className="mb-1.5 bg-[#3DD56D]/12 text-[#3DD56D] border-[#3DD56D]/35 border text-[10px]">
              <CheckCircle2 size={9} className="mr-1" /> Voice enrolled · {profile.enrollment_count} sample{profile.enrollment_count === 1 ? '' : 's'}
            </Badge>
            <p className="text-xs dark:text-neutral-300 text-gray-700">
              Hi <span className="font-semibold">{username}</span>, please say:
            </p>
            <p className="mt-1 px-3 py-1.5 rounded-lg bg-[#3DD56D]/10 border border-[#3DD56D]/30 text-[#3DD56D] font-mono text-[12px]">
              "{profile.voice_phrase}"
            </p>
          </div>

          <div className="flex flex-col items-center mt-2">
            <button
              type="button"
              data-testid={isRecording ? 'voice-stop-button' : 'voice-record-button'}
              onClick={isRecording ? handleStopAndSubmit : handleStartRecord}
              disabled={submitting}
              className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-200 ${
                isRecording
                  ? 'bg-red-500 hover:bg-red-600 shadow-[0_0_0_8px_rgba(239,68,68,0.18)] animate-pulse'
                  : 'bg-[#3DD56D] hover:bg-[#4ce07c] shadow-[0_4px_18px_rgba(61,213,109,0.35)]'
              }`}
            >
              {isRecording ? <Square className="text-white" size={22} fill="currentColor" /> : <Mic className="text-black" size={22} />}
            </button>
            <p className="mt-1.5 text-[11px] dark:text-neutral-400 text-gray-500">
              {isRecording
                ? <span className="text-red-500 font-medium">● Recording · {recordingTime}s — tap to stop &amp; submit</span>
                : (submitting ? 'Verifying…' : 'Tap mic to record')}
            </p>
            {recError && (
              <p className="mt-1 text-[11px] text-red-500 flex items-center gap-1"><AlertCircle size={11} /> {recError}</p>
            )}
            {transcript && !isRecording && (
              <p className="mt-1.5 text-[11px] dark:text-neutral-500 text-gray-500 italic">
                Heard: "<span className="dark:text-neutral-300 text-gray-700">{transcript}</span>"
              </p>
            )}
          </div>

          <div className="flex items-center justify-between pt-2">
            <button
              type="button"
              data-testid="voice-change-user"
              onClick={() => { setProfile(null); setTranscript(''); setStep('username'); }}
              className="text-[11px] dark:text-neutral-500 text-gray-500 hover:text-[#3DD56D] transition-colors"
            >
              ← Change user
            </button>
            <button
              type="button"
              onClick={() => onSwitchToPassword && onSwitchToPassword(username)}
              className="text-[11px] dark:text-neutral-500 text-gray-500 hover:text-[#3DD56D] transition-colors flex items-center gap-1"
            >
              <KeyRound size={11} /> Password
            </button>
          </div>
        </>
      )}

      {step === 'verifying' && (
        <div className="flex flex-col items-center py-6">
          <RefreshCw className="text-[#3DD56D] rcq-spin" size={28} />
          <p className="mt-2 text-xs dark:text-neutral-300 text-gray-700">Sending audio to Sarvam STT…</p>
        </div>
      )}
    </div>
  );
}
